import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { DataUser } from 'src/app/utils/app-interfaces';
import { AUTH_ENDPOINTS } from 'src/environments/api.constants';
import { ROUTES } from 'src/app/utils/routes';
import { APP_CONSTANTS } from 'src/app/app-constants';
import { Roles } from 'src/app/utils/constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public isRefreshingToken = false;

  constructor(private http: HttpClient, private router: Router) { }

  private isLoggedInSubject = new BehaviorSubject<boolean>(
    this.hasValidToken()
  );
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();

  private hasValidToken(): boolean {
    const token = this.getToken();
    return !!token;
  }

  setLoggedIn(value: boolean): void {
    this.isLoggedInSubject.next(value);
  }

  login(email: string, password: string): Observable<{ token: string, refreshToken: string, data: any }> {
    this.removeToken();
    this.removeUserData();
    this.setLoggedIn(false);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    const loginPayload = {
      email,
      password
    };

    return this.http.post<{ token: string, refreshToken: string, data: DataUser }>(AUTH_ENDPOINTS.login, loginPayload, { headers }).pipe(
      map((response) => {
        response.data = { ...response.data, email };
        return response;
      }),
      tap((response) => {
        const token = response.token;
        const refreshToken = response.refreshToken;
        const userData = response.data as DataUser;

        this.saveTokens({ token, refreshToken });
        this.setUserData(userData);
      })
    );
  }

  // refreshToken(): Observable<{ token: string; refreshToken: string }> {
  //   const refreshToken = this.getRefreshToken();
  //   return this.http.get<{ token: string; refreshToken: string }>(
  //     AUTH_ENDPOINTS.refresh,
  //     {
  //       headers: { refreshToken: refreshToken ?? '' }
  //     }
  //   );
  // }

  refreshToken(): Observable<{ token: string; refreshToken: string }> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    return this.http.post<{ token: string; refreshToken: string }>(
      AUTH_ENDPOINTS.refresh,
      {},
      {
        headers: { refreshToken: refreshToken ?? '' }
      })
      .pipe(
        tap((tokens) => {
          this.saveTokens(tokens);
        })
      );
  }

  confirmEmail(userId: number, token: string) : Observable<{userId: number, token: string}> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const loginPayload = {
      userId: userId,
      token
    };

    return this.http.post<{userId: number, token: string}>(AUTH_ENDPOINTS.confirmEmail, loginPayload, { headers }).pipe(
      map(
        (response) => {
          return response;
        }
      ),
    );
  }

  resetPassword(payload: {userId: number, token: string, password: string}) : Observable<string> {
    return this.http.post<string>(AUTH_ENDPOINTS.setPassword, payload, { responseType: 'text' as 'json' })
      .pipe(
        map(response => {
        return response;
      })
    )
  }

  forgotPassword(payload: {email: string}) : Observable<string> {
    return this.http.post(AUTH_ENDPOINTS.forgotPassword, payload, { responseType: 'text' })
      .pipe(map(response => {
        return response;
      })
    )
  }

  saveTokens(tokens: { token: string; refreshToken: string }) {
    sessionStorage.setItem('token', tokens.token);
    sessionStorage.setItem('refreshToken', tokens.refreshToken);
    this.setLoggedIn(true);
  }

  getToken(): string | null {
    return sessionStorage.getItem('token');
  }

  removeToken(): void {
    sessionStorage.removeItem('token');
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem('refreshToken');
  }

  removeRefreshToken(): void {
    sessionStorage.removeItem('refreshToken');
  }

  setUserData(userData: DataUser): void {
    sessionStorage.setItem('userData', JSON.stringify(userData));
  }

  getUserData(): DataUser | null {
    const userData = sessionStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  }

  getUserRole(): string {
    const userData = sessionStorage.getItem('userData');
    const userRole = userData ? JSON.parse(userData) as DataUser : null;
    const userType = Number(userRole?.userType);
    let role = '';
    if (userType === 1) {
      role = Roles.clientAdmin;
    } else if (userType === 2) {
      role = Roles.agent
    } else if (userType === 3) {
      role = Roles.redactor;
    } else if (userType === 4) {
      role = Roles.ocasaAdmin;
    } else if (userType === 5) {
      role = Roles.operator;
    } else if (userType === 6) {
      role = Roles.auditor;
    }
    return role;
  }

  removeUserData(): void {
    sessionStorage.removeItem('userData');
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    return token !== null;
  }

  logout(): void {
    this.setLoggedIn(false);

    const token = this.getToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'accept': '*/*'
    });
    this.http.get(AUTH_ENDPOINTS.logout, { headers }).subscribe({
      next: (response) => {
        this.removeToken();
        this.removeRefreshToken();
        this.removeUserData();
        this.router.navigateByUrl(ROUTES.LOGIN);
      },
      error: (err) => {
        console.error('Logout failed:', err);
      }
    });
  }


  private cleanSession(redirect: boolean) {
    this.removeToken();
    this.removeUserData();
    this.setLoggedIn(false);
    if (redirect) {
      this.router.navigateByUrl(ROUTES.LOGIN);
    }
  }

  private proccessError(error: any) {
    let message = 'Ocurrió un error desconocido';

    if (error.error) {
      message = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
    } else if (error.status) {
      message = `Error ${error.status} - ${error.statusText}`;
    }

    return throwError(() => message);
  }
}
